import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import { WorkflowNode } from "../../types";
import Select from "@/components/Select";
import useChatWindows from "@/hooks/useChatWindows";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

/**
 * SelectPreChatEditor — picks which chat window (and therefore which pre-chat
 * survey, since the pre-chat is configured inside the chat window) runs when
 * the visitor clicks the chat button. Stored on the node as `chatWindowId`
 * so the messaging backend can return the matching pre-chat config to the
 * widget.
 */
export default function SelectPreChatEditor({ node, onUpdate }: Props) {
  const { chatWindows, loading } = useChatWindows();

  const options: Record<string, string> = React.useMemo(() => {
    const map: Record<string, string> = { "": loading ? "Loading…" : "— Select a chat window / pre-chat —" };
    (chatWindows || []).forEach((cw) => {
      map[String(cw.id)] = cw.name || `Chat window #${cw.id}`;
    });
    return map;
  }, [chatWindows, loading]);

  return (
    <FormGroup
      label="Pre-chat Survey (from Chat Window)"
      tooltipText="When the chat button is clicked, launch the pre-chat survey from this chat window before the conversation starts."
    >
      <Select
        options={options}
        value={node.chatWindowId ? String(node.chatWindowId) : ""}
        onChange={(v) => {
          const id = v ? parseInt(v, 10) : undefined;
          onUpdate({ chatWindowId: Number.isFinite(id) ? id : undefined });
        }}
      />
    </FormGroup>
  );
}
